import styled from 'styled-components'
import InfoPopover, { PopLead } from 'features/actions/workspace/InfoPopover'
import { formatDay } from 'features/actions/workspace/remedialDates'

const PRIORITY_NOTES = {
  None: 'No priority was set there, so we gave it the lowest severity. You can change it.',
}

const ImportedRemedialDetails = ({ action, compact = false }) => {
  const imported = action?.metadata?.imported
  if (!imported) return null

  const rows = [
    ['Status there', imported.status],
    ['Closed from its comments', imported.closedFromComment && `${imported.closedFromComment.reason === 'duplicate' ? 'Closed as a duplicate. ' : ''}"${imported.closedFromComment.quote}" (${imported.closedFromComment.by}, ${formatDay(imported.closedFromComment.at)}). It was still open in the previous system, but this comment shows it had been dealt with, so it was marked complete when the records moved.`],
    ['Priority there', imported.priority && (PRIORITY_NOTES[imported.priority] ? `${imported.priority}. ${PRIORITY_NOTES[imported.priority]}` : imported.priority)],
    ['Area', imported.area],
    ['Raised on', imported.raisedDateUnknown ? 'Not recorded in the previous system. It was completed before the records moved there, so only the completion date is known.' : imported.raisedAt && formatDay(imported.raisedAt)],
    ['Work started on', imported.startedAt && formatDay(imported.startedAt)],
    ['Completed on', imported.completedAt && formatDay(imported.completedAt)],
    ['Completed by', imported.completedBy],
    ['Assigned to', (imported.assignees || []).join(', ')],
    ['Recorded on the form', imported.form],
  ].filter(([, value]) => value)

  const missing = Number(imported.filesNotRetrieved || 0)

  return (
    <Wrap $compact={compact}>
      <Head>
        <Title>From the previous system</Title>
        <InfoPopover label='What does this mean?' title='Brought across from the previous system'>
          <PopLead>This remedial was recorded in the system used before Building Passport and was brought across when the records moved.</PopLead>
          <PopLead>The details below are kept exactly as they were recorded there. The description above holds the original observations and actions, and the comments below include everything people wrote there.</PopLead>
          <PopLead>From now on it works like any other remedial: you can change the owner, set a due date, create a task or mark it as complete.</PopLead>
        </InfoPopover>
      </Head>
      <List>
        {rows.map(([label, value]) => (
          <Row key={label}>
            <Key>{label}</Key>
            <Value>{value}</Value>
          </Row>
        ))}
      </List>
      {missing > 0 && (
        <Note role='note'>
          {missing === 1
            ? '1 file attached in the previous system could not be retrieved, so it is not shown here.'
            : `${missing} files attached in the previous system could not be retrieved, so they are not shown here.`}
        </Note>
      )}
    </Wrap>
  )
}

export default ImportedRemedialDetails

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: ${({ $compact }) => ($compact ? '14px' : '15px')};
`

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Title = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
  font-weight: 600;
`

const List = styled.dl`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
`

const Row = styled.div`
  display: flex;
  gap: 18px;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 2px;
  }
`

const Key = styled.dt`
  width: 190px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const Value = styled.dd`
  flex: 1;
  margin: 0;
  color: ${({ theme }) => theme.colors.haiti};
`

const Note = styled.div`
  font-size: 14px;
  line-height: 1.5;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e;
`
